import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetService, userGroupService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  Tooltip,
  FormHelperText,
  Chip,
  LinearProgress,

  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';

// Importar constantes e validações compartilhadas
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  getCategoryInfo,
  getStatusInfo,
  validateAssetData,
  validateLicenseAssetData,
  sanitizeAssetData
} from '../constants/assetConstants';

const LicenseAssetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    category: 'license',
    description: '',
    owner: '',
    location: '',
    status: 'active',
    value: '',
    permission_group_ids: [],
    metadata: {},
    // Campo específico para licenças
    license_key: '',
    expiry_date: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (id) {
      loadAsset(id);
    }
  }, [id]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadAsset = async (assetId) => {
    try {
      setLoading(true);
      const response = await assetService.getAsset(assetId, 'license');
      const asset = response.asset;

      setFormData({
        name: asset.name || '',
        category: 'license',
        description: asset.description || '',
        owner: asset.owner || '',
        location: asset.location || '',
        status: asset.status || 'active',
        value: asset.value || '',
        permission_group_ids: asset.permission_group_ids || (asset.group_id ? [asset.group_id] : []),
        metadata: asset.asset_metadata || {},
        // Campo específico para licenças
        license_key: asset.license_key || '',
        expiry_date: asset.expiry_date ? asset.expiry_date.split('T')[0] : ''
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar ativo:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar ativo', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Validação em tempo real
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    // Validações básicas
    if (name === 'name' && (!value || value.trim().length < 3)) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (name === 'name') {
      delete errors.name;
    }

    if (name === 'value' && value && isNaN(parseFloat(value))) {
      errors.value = 'Valor deve ser um número válido';
    } else if (name === 'value') {
      delete errors.value;
    }

    // Validação específica para chave da licença
    if (name === 'license_key' && (!value || value.trim().length < 5)) {
      errors.license_key = 'Chave da licença deve ter pelo menos 5 caracteres';
    } else if (name === 'license_key') {
      delete errors.license_key;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handler com validação
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    // Validar campo em tempo real
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obrigatórios
    if (!formData.name || formData.name.trim().length < 3) {
      setValidationErrors(prev => ({ ...prev, name: 'Nome deve ter pelo menos 3 caracteres' }));
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    if (!formData.license_key || formData.license_key.trim().length < 5) {
      setValidationErrors(prev => ({ ...prev, license_key: 'Chave da licença deve ter pelo menos 5 caracteres' }));
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        category: 'license',
        description: formData.description,
        owner: formData.owner,
        location: formData.location,
        status: formData.status,
        value: formData.value ? parseFloat(formData.value) : null,
        permission_group_ids: formData.permission_group_ids || [],
        metadata: { ...(formData.metadata || {}) },
        // Campo específico para licenças
        license_key: formData.license_key,
        expiry_date: formData.expiry_date || null
      };

      if (isEdit && id) {
        await assetService.updateAsset(id, submitData);
        setSnackbar({ open: true, message: 'Licença atualizada com sucesso!', severity: 'success' });
      } else {
        await assetService.createAsset(submitData);
        setSnackbar({ open: true, message: 'Licença criada com sucesso!', severity: 'success' });
      }

      setTimeout(() => navigate('/assets/category/license'), 1500);
    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || (isEdit ? 'Erro ao atualizar licença' : 'Erro ao criar licença'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentCategory = ASSET_CATEGORIES['license'];

  // Loading state
  if (loading && !isEdit) {
    return (
      <Layout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6">Salvando licença...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>Voltar</Button>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <AssignmentIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                {isEdit ? 'Editar Licença' : 'Nova Licença'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentCategory?.description || 'Licenças de software'}
              </Typography>
            </Box>
          </Box>

          {/* Indicador de progresso */}
          {Object.keys(validationErrors).length > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${Object.keys(validationErrors).length} erro(s) de validação`}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>



        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informações Básicas - Step 1 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                      <InfoIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Básicas</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome da Licença"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled>
                        <InputLabel>Categoria</InputLabel>
                        <Select value="license" label="Categoria">
                          <MenuItem value="license">Licenças</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Status</InputLabel>
                        <Select name="status" value={formData.status} onChange={handleChange} label="Status">
                          {Object.entries(ASSET_STATUSES).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              <Chip label={config.label} color={config.color} size="small" sx={{ mr: 1 }} />
                              {config.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Descrição"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Descreva o software ou serviço licenciado..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Licença - Step 2 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                      <AssignmentIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Detalhes da Licença</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Chave da Licença *"
                        name="license_key"
                        value={formData.license_key}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        error={!!validationErrors.license_key}
                        helperText={validationErrors.license_key || 'Cole aqui a chave de licença completa'}
                        placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Data de Expiração"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Informações Adicionais - Step 3 */}
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Adicionais</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Valor (R$)"
                        name="value"
                        type="number"
                        step="0.01"
                        value={formData.value}
                        onChange={handleChange}
                        disabled={loading}
                        error={!!validationErrors.value}
                        helperText={validationErrors.value}
                        InputProps={{
                          startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Localização/Sistema"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        placeholder="Ex: Servidor X, Estação Y..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Responsável"
                        name="owner"
                        value={formData.owner}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Grupos de Permissão</InputLabel>
                        <Select
                          name="permission_group_ids"
                          value={formData.permission_group_ids}
                          onChange={handleChange}
                          label="Grupos de Permissão"
                          multiple
                        >
                          {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Card de Ações e Validação */}
              <Card elevation={2} sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Ações</Typography>

                  {/* Resumo de validação */}
                  <Box sx={{ mb: 2 }}>
                    {Object.keys(validationErrors).length === 0 ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        Todos os campos obrigatórios foram preenchidos!
                      </Alert>
                    ) : (
                      <Alert severity="warning" icon={<WarningIcon />}>
                        {Object.keys(validationErrors).length} campo(s) precisam de atenção
                      </Alert>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={loading || Object.keys(validationErrors).length > 0}
                  >
                    {loading ? 'Salvando...' : 'Salvar Licença'}
                  </Button>

                  {Object.keys(validationErrors).length > 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      Corrija os erros acima antes de salvar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar melhorado */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default LicenseAssetForm;
